/**
 * @description Utilitários para geração de carteirinhas de pets
 * @module petCardUtils
 * @since Outubro 2025
 */

import dayjs from "dayjs";
import logoVet from "../../assets/Logo.png";

export interface IPetCardData {
  id: string;
  name: string;
  species: string;
  breed: string;
  birthDate: string;
  gender: string;
  identifier: string;
  owner: string;
  createdAt: string;
  qrCodeBase64: string;
}

/**
 * Gera uma carteirinha de pet usando Canvas
 * @param petData - Dados do pet para incluir na carteirinha
 * @returns Promise com Canvas element com a carteirinha renderizada
 */
export const generatePetCard = async (
  petData: IPetCardData
): Promise<HTMLCanvasElement> => {
  // Criar canvas para a carteirinha com tamanho otimizado
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context não disponível");

  // Configurações do canvas
  canvas.width = 500;
  canvas.height = 300;

  // Fundo branco limpo
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Borda verde elegante
  ctx.strokeStyle = "#00A884";
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.roundRect(5, 5, canvas.width - 10, canvas.height - 10, 12);
  ctx.stroke();

  // Borda inferior do header (linha separadora)
  const headerBottom = 125;
  ctx.strokeStyle = "#e0e0e0";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(10, headerBottom);
  ctx.lineTo(canvas.width - 10, headerBottom);
  ctx.stroke();

  // Carregar e desenhar logo FluxVet - MÁXIMO sem nenhum espaço
  await new Promise<void>((resolve) => {
    const logo = new Image();
    logo.onload = () => {
      // Logo MÁXIMO - altura máxima sem espaços em branco
      const logoHeight = 125;
      const logoWidth = (logo.width / logo.height) * logoHeight;

      const logoX = (canvas.width - logoWidth) / 2;
      const logoY = 5; // Mínimo espaço superior

      ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
      resolve();
    };
    logo.onerror = () => {
      ctx.fillStyle = "#00A884";
      ctx.font = "bold 50px Arial";
      ctx.textAlign = "center";
      ctx.fillText("FLUXVET", canvas.width / 2, 70);
      resolve();
    };
    logo.src = logoVet;
  });

  // Nome do pet - PRETO
  const nameY = 147;
  const nameText = petData.name.toUpperCase();

  ctx.fillStyle = "#1a1a1a";
  ctx.font = "bold 24px Arial";
  ctx.textAlign = "left";
  ctx.fillText(nameText, 25, nameY);

  // Informações organizadas em lista limpa
  const startY = 170;
  const lineHeight = 22;
  let currentY = startY;

  // ID do pet - estilo destacado do MyPets.tsx
  const idText = `ID: ${petData.identifier}`;
  ctx.font = "bold 12px monospace";
  const idWidth = ctx.measureText(idText).width;

  // Fundo verde claro para o ID
  ctx.fillStyle = "rgba(0, 168, 132, 0.1)";
  ctx.beginPath();
  ctx.roundRect(22, currentY - 12, idWidth + 16, 20, 4);
  ctx.fill();

  // Texto do ID
  ctx.fillStyle = "#00A884";
  ctx.font = "bold 12px monospace";
  ctx.textAlign = "left";
  ctx.fillText(idText, 30, currentY);
  currentY += 28; // Espaço maior após o ID destacado

  // Função para desenhar linha de informação
  const drawInfo = (label: string, value: string, y: number) => {
    // Label
    ctx.fillStyle = "#666666";
    ctx.font = "12px Arial";
    ctx.textAlign = "left";
    ctx.fillText(label, 25, y);

    // Separador
    ctx.fillText(":", 110, y);

    // Valor
    ctx.fillStyle = "#1a1a1a";
    ctx.font = "bold 13px Arial";
    ctx.fillText(value, 120, y);
  };

  // Espécie
  drawInfo("Espécie", petData.species, currentY);
  currentY += lineHeight;

  // Raça (se disponível)
  if (petData.breed && petData.breed !== "Não informado") {
    drawInfo("Raça", petData.breed, currentY);
    currentY += lineHeight;
  }

  // Sexo
  drawInfo("Sexo", petData.gender, currentY);
  currentY += lineHeight;

  // Nascimento
  const birthDateFormatted =
    petData.birthDate && dayjs(petData.birthDate).isValid()
      ? dayjs(petData.birthDate).format("DD/MM/YYYY")
      : "-";
  drawInfo("Nascimento", birthDateFormatted, currentY);

  // QR Code - usando a imagem base64 fornecida
  const qrSize = 140;
  const qrX = canvas.width - qrSize - 30;
  const qrY = 130;

  // Carregar e desenhar QR Code a partir do base64
  await new Promise<void>((resolve) => {
    const qrImage = new Image();
    qrImage.onload = () => {
      // Desenhar QR Code com borda
      // ctx.strokeStyle = "#00A884";
      // ctx.lineWidth = 3;
      // ctx.beginPath();
      // ctx.roundRect(qrX, qrY, qrSize, qrSize, 8);
      // ctx.stroke();

      // Desenhar a imagem do QR Code
      ctx.drawImage(qrImage, qrX + 5, qrY + 5, qrSize - 10, qrSize - 10);
      resolve();
    };
    qrImage.onerror = () => {
      // Fallback em caso de erro no carregamento do QR Code
      ctx.strokeStyle = "#00A884";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.roundRect(qrX, qrY, qrSize, qrSize, 8);
      ctx.stroke();

      ctx.fillStyle = "#f8f9fa";
      ctx.fillRect(qrX + 3, qrY + 3, qrSize - 6, qrSize - 6);

      ctx.fillStyle = "#00A884";
      ctx.font = "bold 16px Arial";
      ctx.textAlign = "center";
      ctx.fillText("QR CODE", qrX + qrSize / 2, qrY + qrSize / 2);
      resolve();
    };
    qrImage.src = petData.qrCodeBase64;
  });

  // Rodapé com informações - mais compacto
  ctx.fillStyle = "#999999";
  ctx.font = "9px Arial";
  ctx.textAlign = "left";
  ctx.fillText(
    `Emitido: ${dayjs().format("DD/MM/YYYY HH:mm")}`,
    15,
    canvas.height - 15
  );

  ctx.textAlign = "right";
  const tutorText = `Tutor: ${petData.owner}`;
  const maxTutorWidth = 200;
  const tutorWidth = ctx.measureText(tutorText).width;
  if (tutorWidth > maxTutorWidth) {
    const truncatedTutor = petData.owner.substring(0, 25) + "...";
    ctx.fillText(
      `Tutor: ${truncatedTutor}`,
      canvas.width - 15,
      canvas.height - 15
    );
  } else {
    ctx.fillText(tutorText, canvas.width - 15, canvas.height - 15);
  }

  return canvas;
};

/**
 * Gera um QR Code real usando uma biblioteca externa (opcional)
 * @param data - Dados para codificar no QR
 * @returns Promise com o canvas do QR code
 */
export const generateQRCode = async (
  data: string
): Promise<HTMLCanvasElement> => {
  // Implementação futura com biblioteca de QR code
  // Por enquanto, retorna um canvas vazio
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context não disponível");

  canvas.width = 80;
  canvas.height = 80;

  // Placeholder
  ctx.fillStyle = "#f5f5f5";
  ctx.fillRect(0, 0, 80, 80);
  ctx.strokeStyle = "#00A884";
  ctx.lineWidth = 2;
  ctx.strokeRect(0, 0, 80, 80);

  return canvas;
};

/**
 * Valida se os dados do pet são suficientes para gerar uma carteirinha
 * @param petData - Dados do pet
 * @returns true se válido, false caso contrário
 */
export const validatePetData = (petData: Partial<IPetCardData>): boolean => {
  const requiredFields = ["id", "name", "species", "gender", "identifier"];
  return requiredFields.every((field) => petData[field as keyof IPetCardData]);
};

/**
 * Formata o nome do arquivo para download
 * @param petName - Nome do pet
 * @returns Nome formatado para o arquivo
 */
export const formatFileName = (petName: string): string => {
  return `carteirinha-${petName.replace(/\s+/g, "-").toLowerCase()}.png`;
};
